import { JuloUtils } from "../common"
import { Product } from "../pos"
import { AcceptAddonOrder, AddonOrderToAccept } from "./addon-order"
import { Discount } from "./discount"
import { ProductChoice } from "./evaluated-service-bike-part"
import {
  FreshServiceProposal,
  ServiceProposalFee,
  ServiceProposalPackage,
  SubmitFreshServiceProposal,
} from "./fresh-service-proposal"
import { PriceUtils, SERVICE_VAT } from "./price-utils"
import {
  isProposalWashInPackages,
  isWashInPackages,
} from "./proposal-wash-utils"
import {
  Receipt,
  ReceiptFee,
  ReceiptManualAction,
  ReceiptPackage,
  ReceiptPackagePartAction,
  ReceiptPackageProduct,
  ReceiptPackageWash,
  ReceiptPartAction,
  ReceiptProduct,
  ReceiptWash,
} from "./receipt"
import { ServiceAction } from "./service-part-action"
import { getCheapestAlternative } from "./service-proposal-grouping"
import {
  ServiceProposalUpsell,
  getUpsellDiscounts,
  getUpsellProductPricing,
  resolveSelectedUpsellProducts,
} from "./upsell"

/**
 * How a product choice the customer has not answered yet is priced.
 *
 * The mechanic-facing views price it at the recommendation, which is what the
 * proposal actually says. The customer-facing screen prices it at the cheapest
 * alternative, so the "Från X kr" it puts under the total is a floor the
 * customer can reach rather than a number that can still go down.
 */
export type UndecidedChoicePricing = "RECOMMENDED" | "CHEAPEST"

export interface ProposalToReceiptOptions {
  undecidedChoicePricing?: UndecidedChoicePricing
}

export class ReceiptMapper {
  private static findRequiredItem<T>(
    indexed: Record<string, T>,
    key: string,
    itemType: string,
  ): T {
    const item = indexed[key]
    if (!item) {
      const msg = `Couldn't find ${itemType} with key ${key}`
      console.error(msg)
      throw new Error(msg)
    }
    return item
  }

  /**
   * A standalone wash as a receipt line. `price` is what the wash costs after
   * its own discount, which is what every total sums.
   */
  static washToReceiptWash(wash: {
    snapshotWashId: string
    name: string
    price: number
    laborPriceDiscount: Discount | null
  }): ReceiptWash {
    return {
      snapshotId: wash.snapshotWashId,
      title: wash.name,
      price: PriceUtils.applyDiscount(wash.price, wash.laborPriceDiscount),
      priceOriginal: wash.price,
      priceDiscount: wash.laborPriceDiscount,
    }
  }

  /**
   * A fee on a sent proposal or addon order. Those are sent before a job is
   * Färdig, so they never carry the parking fee, the only discounted fee.
   */
  static proposalFeeToReceiptFee(fee: ServiceProposalFee): ReceiptFee {
    return {
      id: fee.snapshotId,
      title: fee.title,
      price: fee.cost,
      priceOriginal: fee.cost,
      priceDiscount: null,
      isParkingFee: false,
      description: fee.description,
      originalFeeId: fee.originalFeeId,
    }
  }

  static productToReceiptProduct(product: Product): ReceiptProduct {
    return {
      id: product.id,
      externalId: product.externalId,
      ean: product.ean,
      articleNumber: product.articleNumber,
      acquisitionCost: product.acquisitionCost,
      title: product.title,
      price: product.price,
      quantity: product.quantity ?? 1,
      discount: product.discount ?? null,
    }
  }

  private static findSelectedProduct(
    alternatives: Product[],
    chosenProductId: string,
  ): ReceiptProduct {
    const selectedProduct = alternatives.find(
      (productAlternative) => productAlternative.id === chosenProductId,
    )
    if (!selectedProduct) {
      const msg = `Couldn't find selected product with product id ${chosenProductId}`
      console.error(msg)
      throw new Error(msg)
    }
    return ReceiptMapper.productToReceiptProduct(selectedProduct)
  }

  /**
   * Decisions are read one choice at a time. An action can hold several choices
   * and the customer answers them one by one, so "some decisions recorded" says
   * nothing about whether *this* choice has been answered.
   */
  private static processProductChoices(
    productChoices: ProductChoice[],
    productDecisions?: Record<string, string>,
    undecidedPricing: UndecidedChoicePricing = "RECOMMENDED",
  ): ReceiptProduct[] {
    const products: ReceiptProduct[] = []

    for (const choice of productChoices) {
      const { id: choiceId, recommendedAlternative, otherAlternatives } = choice
      const chosenProductId = productDecisions?.[choiceId]

      if (chosenProductId) {
        products.push(
          ReceiptMapper.findSelectedProduct(
            [recommendedAlternative, ...otherAlternatives],
            chosenProductId,
          ),
        )
        continue
      }

      products.push(
        ReceiptMapper.productToReceiptProduct(
          undecidedPricing === "CHEAPEST"
            ? getCheapestAlternative(choice)
            : recommendedAlternative,
        ),
      )
    }

    return products
  }

  /**
   * The upsells the customer added, as receipt rows priced with the store's
   * "here and now" discount — the same rows the job gets on acceptance.
   */
  static upsellsToReceiptManualActions(
    upsell: ServiceProposalUpsell | null | undefined,
    selectedUpsellProductIds: string[],
  ): ReceiptManualAction[] {
    return resolveSelectedUpsellProducts(upsell, selectedUpsellProductIds).map(
      ({ product }) => {
        const pricing = getUpsellProductPricing(
          product,
          upsell?.discount ?? null,
        )
        const { laborDiscount, productDiscount } = getUpsellDiscounts(
          upsell?.discount ?? null,
        )
        const products: ReceiptProduct[] = [
          {
            id: product.id,
            externalId: null,
            ean: null,
            articleNumber: null,
            acquisitionCost: null,
            title: product.title,
            price: pricing.productPrice,
            quantity: 1,
            discount: productDiscount,
          },
        ]
        return {
          id: product.id,
          // The product's own title: these rows only live in the browser and
          // the job's work-order title is written in Swedish by the API.
          title: product.title,
          isUpsell: true,
          products,
          laborPrice: pricing.laborPriceNow,
          laborPriceOriginal: pricing.laborPrice,
          laborPriceAfterDiscount: PriceUtils.getDiscountedPriceOrNull(
            pricing.laborPrice,
            laborDiscount,
          ),
          laborPriceDiscount: laborDiscount,
          totalProductCost: pricing.productPriceNow,
          totalCost: pricing.totalNow,
        }
      },
    )
  }

  static proposalToReceipt(
    proposal: FreshServiceProposal,
    submission?: SubmitFreshServiceProposal,
    { undecidedChoicePricing = "RECOMMENDED" }: ProposalToReceiptOptions = {},
  ): Receipt {
    const packages: ReceiptPackage[] = []
    const partActions: ReceiptPartAction[] = []
    const manualActions: ReceiptManualAction[] = []
    const fees: ReceiptFee[] = []
    let wash: ReceiptWash | null = null
    let totalCost = 0

    if (submission) {
      // Handle submitted selections
      const indexedPackages = JuloUtils.indexBy(
        proposal.packages,
        "originalPackageId",
      )
      for (const pkg of submission.selectedPackages) {
        const {
          originalPackageId,
          selectedPartActions,
          selectedWashOriginalId,
        } = pkg
        const pkgInfo = indexedPackages[originalPackageId]
        // Skip packages not found in the proposal (can happen transiently
        // when the proposal is being refetched after optimization)
        if (!pkgInfo) continue

        const {
          name,
          laborPrice,
          laborPriceDiscount,
          includedMaterialCost,
          partActions,
          wash,
        } = pkgInfo
        const products: ReceiptPackageProduct[] = []
        const packagePartActions: ReceiptPackagePartAction[] = []
        let packageWash: ReceiptPackageWash | null = null

        if (
          wash &&
          selectedWashOriginalId &&
          wash.originalWashId === selectedWashOriginalId
        ) {
          packageWash = {
            snapshotId: wash.snapshotWashId,
            originalWashId: wash.originalWashId,
            title: wash.name,
          }
        }

        const indexedPartActions = JuloUtils.indexBy(partActions, "partId")

        for (const partAction of selectedPartActions) {
          const { partId, productDecisions } = partAction
          const partInfo = ReceiptMapper.findRequiredItem(
            indexedPartActions,
            partId,
            "part",
          )

          const {
            products: partActionProducts,
            productChoices,
            partName,
          } = partInfo

          packagePartActions.push({
            partId,
            partName,
            partAction: partInfo.action,
            actionLabel: partInfo.actionLabel,
          })

          products.push(
            ...partActionProducts.map((product) => ({
              id: product.id,
              externalId: product.externalId,
              ean: product.ean,
              articleNumber: product.articleNumber,
              acquisitionCost: product.acquisitionCost,
              title: product.title,
              price: product.price,
              quantity: product.quantity ?? 1,
              discount: product.discount ?? null,
              partId,
              partName,
              partAction: partInfo.action,
              actionLabel: partInfo.actionLabel,
            })),
          )

          const choiceProducts = ReceiptMapper.processProductChoices(
            productChoices,
            productDecisions,
            undecidedChoicePricing,
          )
          products.push(
            ...choiceProducts.map((product) => ({
              ...product,
              partId,
              partName,
              partAction: partInfo.action,
              actionLabel: partInfo.actionLabel,
            })),
          )
        }

        const totalProductCost = PriceUtils.getProductsCost(products)
        const laborPriceAfterDiscount = PriceUtils.getDiscountedPriceOrNull(
          laborPrice,
          laborPriceDiscount,
        )
        const { total: totalPackageCost, materialLaborDeduction } =
          PriceUtils.getPackageTotal({
            laborPrice,
            laborPriceDiscount,
            totalProductCost,
            includedMaterialCost,
          })
        packages.push({
          originalPackageId,
          snapshotId: originalPackageId, // This is fine since it's only for the preview
          title: name,
          products,
          partActions: packagePartActions,
          availableActions: [],
          wash: packageWash,
          laborPrice,
          laborPriceAfterDiscount,
          laborPriceDiscount,
          totalProductCost,
          includedMaterialCost,
          materialLaborDeduction,
          totalCost: totalPackageCost,
        })

        totalCost += totalPackageCost
      }

      // Handle standalone part actions
      const indexedProposalPartActions = JuloUtils.indexBy(
        proposal.partActions,
        "partId",
      )
      for (const selectedPartAction of submission.selectedPartActions) {
        const { partId, productDecisions } = selectedPartAction
        const partActionInfo = ReceiptMapper.findRequiredItem(
          indexedProposalPartActions,
          partId,
          "part action",
        )

        const {
          products: partActionProducts,
          productChoices,
          partName,
          action,
          laborPrice,
          laborPriceDiscount,
          comment,
        } = partActionInfo

        const products: ReceiptProduct[] = [
          ...partActionProducts.map((product) => ({
            id: product.id,
            externalId: product.externalId,
            ean: product.ean,
            articleNumber: product.articleNumber,
            acquisitionCost: product.acquisitionCost,
            title: product.title,
            price: product.price,
            quantity: product.quantity ?? 1,
            discount: product.discount ?? null,
          })),
          ...ReceiptMapper.processProductChoices(
            productChoices,
            productDecisions,
            undecidedChoicePricing,
          ),
        ]

        const discountedLaborPrice = PriceUtils.applyDiscount(
          laborPrice,
          laborPriceDiscount,
        )
        const totalProductCost = PriceUtils.getProductsCost(products)
        const totalPartActionCost = discountedLaborPrice + totalProductCost

        partActions.push({
          partId,
          partName,
          partAction: action,
          actionLabel: partActionInfo.actionLabel,
          comment: comment ?? undefined,
          products,
          laborPrice: discountedLaborPrice,
          laborPriceOriginal: laborPrice,
          laborPriceAfterDiscount:
            laborPriceDiscount != null
              ? PriceUtils.getDiscountedPriceOrNull(
                  laborPrice,
                  laborPriceDiscount,
                )
              : null,
          laborPriceDiscount: laborPriceDiscount ?? null,
          totalProductCost,
          totalCost: totalPartActionCost,
        })

        totalCost += totalPartActionCost
      }

      // Handle standalone manual actions
      const indexedProposalManualActions = JuloUtils.indexBy(
        proposal.manualActions,
        "id",
      )
      for (const selectedManualAction of submission.selectedManualActions) {
        const { id, productDecisions } = selectedManualAction
        const manualActionInfo = ReceiptMapper.findRequiredItem(
          indexedProposalManualActions,
          id,
          "manual action",
        )

        const {
          products: manualActionProducts,
          productChoices,
          title,
          laborPrice,
          laborPriceDiscount,
          comment,
        } = manualActionInfo

        const products: ReceiptProduct[] = [
          ...manualActionProducts.map((product) => ({
            id: product.id,
            externalId: product.externalId,
            ean: product.ean,
            articleNumber: product.articleNumber,
            acquisitionCost: product.acquisitionCost,
            title: product.title,
            price: product.price,
            quantity: product.quantity ?? 1,
            discount: product.discount ?? null,
          })),
          ...ReceiptMapper.processProductChoices(
            productChoices,
            productDecisions,
            undecidedChoicePricing,
          ),
        ]

        const effectiveLaborPrice = PriceUtils.applyDiscount(
          laborPrice,
          laborPriceDiscount ?? null,
        )
        const totalProductCost = PriceUtils.getProductsCost(products)
        const totalManualActionCost = effectiveLaborPrice + totalProductCost

        manualActions.push({
          id,
          title,
          comment: comment ?? undefined,
          products,
          laborPrice: effectiveLaborPrice,
          laborPriceOriginal: laborPrice,
          laborPriceAfterDiscount: PriceUtils.getDiscountedPriceOrNull(
            laborPrice,
            laborPriceDiscount ?? null,
          ),
          laborPriceDiscount: laborPriceDiscount ?? null,
          totalProductCost,
          totalCost: totalManualActionCost,
        })

        totalCost += totalManualActionCost
      }

      // Handle standalone wash
      const washExplicitlyDeselected = submission.isWashDeselected

      // Check if any selected package already includes a wash that matches
      // an available wash (from alternatives or single wash)
      const selectedPkgData = submission.selectedPackages
        .map((sel) => indexedPackages[sel.originalPackageId])
        .filter((pkg): pkg is ServiceProposalPackage => pkg !== undefined)
      const isWashAlreadyInPackage = isProposalWashInPackages(
        proposal,
        selectedPkgData,
      )

      if (!isWashAlreadyInPackage && submission.selectedWashOriginalId) {
        // First check if the selected wash is from the single wash
        if (
          proposal.wash &&
          proposal.wash.originalWashId === submission.selectedWashOriginalId
        ) {
          wash = ReceiptMapper.washToReceiptWash(proposal.wash)
          totalCost += wash.price
        } else if (proposal.washAlternatives) {
          // Check if the selected wash is from wash alternatives
          const selectedAlt = proposal.washAlternatives.alternatives.find(
            (alt) => alt.originalWashId === submission.selectedWashOriginalId,
          )
          if (selectedAlt) {
            wash = ReceiptMapper.washToReceiptWash(selectedAlt)
            totalCost += wash.price
          }
        }
      } else if (
        !isWashAlreadyInPackage &&
        proposal.washAlternatives &&
        proposal.washAlternatives.isMandatory &&
        !washExplicitlyDeselected
      ) {
        // A mandatory choice the customer has not answered yet. The recommended
        // alternative stands in so the total reads as a "från"-price; an
        // optional group left unanswered simply costs nothing.
        const recommendedWash = proposal.washAlternatives.alternatives.find(
          (alt) => alt.isRecommended,
        )
        if (recommendedWash) {
          wash = ReceiptMapper.washToReceiptWash(recommendedWash)
          totalCost += wash.price
        }
      }
    } else {
      // Handle case when submission is undefined - include everything with recommended alternatives

      // Include all packages
      for (const pkg of proposal.packages) {
        const {
          snapshotPackageId,
          name,
          laborPrice,
          laborPriceDiscount,
          includedMaterialCost,
          partActions,
          wash,
        } = pkg
        const products: ReceiptPackageProduct[] = []
        const packagePartActions: ReceiptPackagePartAction[] = []
        let packageWash: ReceiptPackageWash | null = null

        // Include wash if present
        if (wash) {
          packageWash = {
            snapshotId: wash.snapshotWashId,
            originalWashId: wash.originalWashId,
            title: wash.name,
          }
        }

        // Include all part actions from the package
        for (const partAction of partActions) {
          const {
            partId,
            partName,
            action,
            products: partActionProducts,
            productChoices,
          } = partAction

          packagePartActions.push({
            partId,
            partName,
            partAction: action,
            actionLabel: partAction.actionLabel,
          })

          products.push(
            ...partActionProducts.map((product) => ({
              id: product.id,
              externalId: product.externalId,
              ean: product.ean,
              articleNumber: product.articleNumber,
              acquisitionCost: product.acquisitionCost,
              title: product.title,
              price: product.price,
              quantity: product.quantity ?? 1,
              discount: product.discount ?? null,
              partId,
              partName,
              partAction: action,
              actionLabel: partAction.actionLabel,
            })),
          )

          const choiceProducts =
            ReceiptMapper.processProductChoices(productChoices)
          products.push(
            ...choiceProducts.map((product) => ({
              ...product,
              partId,
              partName,
              partAction: action,
              actionLabel: partAction.actionLabel,
            })),
          )
        }

        const totalProductCost = PriceUtils.getProductsCost(products)
        const laborPriceAfterDiscount = PriceUtils.getDiscountedPriceOrNull(
          laborPrice,
          laborPriceDiscount,
        )
        const { total: totalPackageCost, materialLaborDeduction } =
          PriceUtils.getPackageTotal({
            laborPrice,
            laborPriceDiscount,
            totalProductCost,
            includedMaterialCost,
          })
        packages.push({
          originalPackageId: pkg.originalPackageId,
          snapshotId: snapshotPackageId,
          title: name,
          products,
          partActions: packagePartActions,
          availableActions: [],
          wash: packageWash,
          laborPrice,
          laborPriceAfterDiscount,
          laborPriceDiscount,
          totalProductCost,
          includedMaterialCost,
          materialLaborDeduction,
          totalCost: totalPackageCost,
        })

        totalCost += totalPackageCost
      }

      // Include all standalone part actions (excluding DO_NOTHING)
      for (const partAction of proposal.partActions) {
        // Skip DO_NOTHING actions - they're only for comments display
        if (partAction.action === ServiceAction.DO_NOTHING) continue

        if (
          packages.some((pkg) =>
            pkg.partActions.some(
              (pkgAction) => pkgAction.partId === partAction.partId,
            ),
          )
        )
          continue

        const {
          partId,
          partName,
          action,
          laborPrice,
          laborPriceDiscount,
          products: partActionProducts,
          productChoices,
          comment,
        } = partAction

        const products: ReceiptProduct[] = [
          ...partActionProducts.map((product) => ({
            id: product.id,
            externalId: product.externalId,
            ean: product.ean,
            articleNumber: product.articleNumber,
            acquisitionCost: product.acquisitionCost,
            title: product.title,
            price: product.price,
            quantity: product.quantity ?? 1,
            discount: product.discount ?? null,
          })),
          ...ReceiptMapper.processProductChoices(productChoices),
        ]

        const discountedLaborPrice = PriceUtils.applyDiscount(
          laborPrice,
          laborPriceDiscount,
        )
        const totalProductCost = PriceUtils.getProductsCost(products)
        const totalPartActionCost = discountedLaborPrice + totalProductCost

        partActions.push({
          partId,
          partName,
          partAction: action,
          actionLabel: partAction.actionLabel,
          comment: comment ?? undefined,
          products,
          laborPrice: discountedLaborPrice,
          laborPriceOriginal: laborPrice,
          laborPriceAfterDiscount:
            laborPriceDiscount != null
              ? PriceUtils.getDiscountedPriceOrNull(
                  laborPrice,
                  laborPriceDiscount,
                )
              : null,
          laborPriceDiscount: laborPriceDiscount ?? null,
          totalProductCost,
          totalCost: totalPartActionCost,
        })

        totalCost += totalPartActionCost
      }

      // Include all standalone manual actions
      for (const manualAction of proposal.manualActions) {
        const {
          id,
          title,
          laborPrice,
          laborPriceDiscount,
          products: manualActionProducts,
          productChoices,
          comment,
        } = manualAction

        const products: ReceiptProduct[] = [
          ...manualActionProducts.map((product) => ({
            id: product.id,
            externalId: product.externalId,
            ean: product.ean,
            articleNumber: product.articleNumber,
            acquisitionCost: product.acquisitionCost,
            title: product.title,
            price: product.price,
            quantity: product.quantity ?? 1,
            discount: product.discount ?? null,
          })),
          ...ReceiptMapper.processProductChoices(productChoices),
        ]

        const effectiveLaborPrice = PriceUtils.applyDiscount(
          laborPrice,
          laborPriceDiscount ?? null,
        )
        const totalProductCost = PriceUtils.getProductsCost(products)
        const totalManualActionCost = effectiveLaborPrice + totalProductCost

        manualActions.push({
          id,
          title,
          comment: comment ?? undefined,
          products,
          laborPrice: effectiveLaborPrice,
          laborPriceOriginal: laborPrice,
          laborPriceAfterDiscount: PriceUtils.getDiscountedPriceOrNull(
            laborPrice,
            laborPriceDiscount ?? null,
          ),
          laborPriceDiscount: laborPriceDiscount ?? null,
          totalProductCost,
          totalCost: totalManualActionCost,
        })

        totalCost += totalManualActionCost
      }

      // Include standalone wash if present - prioritize wash alternatives over single wash
      // Compare by originalWashId (not snapshotId) to correctly deduplicate when a
      // wash alternative and a package's wash represent the same underlying wash service
      if (proposal.washAlternatives) {
        // Use the recommended wash from alternatives
        const recommendedWash = proposal.washAlternatives.alternatives.find(
          (alt) => alt.isRecommended,
        )
        if (
          recommendedWash &&
          !isWashInPackages(recommendedWash.originalWashId, proposal.packages)
        ) {
          wash = ReceiptMapper.washToReceiptWash(recommendedWash)
          totalCost += wash.price
        }
      } else if (
        proposal.wash &&
        !isWashInPackages(proposal.wash.originalWashId, proposal.packages)
      ) {
        wash = ReceiptMapper.washToReceiptWash(proposal.wash)
        totalCost += wash.price
      }
    }

    if (submission) {
      for (const upsellAction of ReceiptMapper.upsellsToReceiptManualActions(
        proposal.upsell,
        submission.selectedUpsellProductIds ?? [],
      )) {
        manualActions.push(upsellAction)
        totalCost += upsellAction.totalCost
      }
    }

    // Handle fees. A 0 kr fee isn't a line on the receipt.
    for (const fee of proposal.fees.filter((fee) => fee.cost > 0)) {
      fees.push(ReceiptMapper.proposalFeeToReceiptFee(fee))
      totalCost += fee.cost
    }

    const effectiveLaborCost =
      packages.reduce(
        (sum, pkg) => sum + PriceUtils.getPackageLaborPrice(pkg),
        0,
      ) +
      partActions.reduce((sum, a) => sum + a.laborPrice, 0) +
      manualActions.reduce((sum, a) => sum + a.laborPrice, 0) +
      (wash?.price ?? 0) +
      fees.reduce((sum, f) => sum + f.price, 0)

    const totalProductsCostSum =
      packages.reduce((sum, pkg) => sum + pkg.totalProductCost, 0) +
      partActions.reduce((sum, a) => sum + a.totalProductCost, 0) +
      manualActions.reduce((sum, a) => sum + a.totalProductCost, 0)

    return {
      packages,
      partActions,
      manualActions,
      wash,
      fees,
      totalCost,
      orderDiscount: null,
      totalCostAfterOrderDiscount: null,
      laborDiscount: null,
      laborCostAfterDiscount: null,
      productsDiscount: null,
      productsCostAfterDiscount: null,
      vat: Math.round(totalCost * (SERVICE_VAT / 100) * 100) / 100,
      customerBudget: null,
      effectiveLaborCost,
      effectiveProductsCost: totalProductsCostSum,
      // Matches `receipt.service.ts` semantics: `totalDiscountGiven` measures
      // only discounts applied on top (labor/products/order). None of those apply
      // in this proposal-to-receipt path, so this is always 0.
      totalDiscountGiven: 0,
    }
  }

  static addonOrderToReceipt(
    proposal: AddonOrderToAccept,
    submission: AcceptAddonOrder,
  ): Receipt {
    const partActions: ReceiptPartAction[] = []
    const manualActions: ReceiptManualAction[] = []
    const packages: ReceiptPackage[] = []
    let wash: ReceiptWash | null = null
    let totalCost = 0

    // Process selected parts
    const acceptedPartIds = new Set(submission.acceptedPartIds)
    for (const part of proposal.parts) {
      if (!acceptedPartIds.has(part.partId)) continue

      const products: ReceiptProduct[] = [
        ...part.products.map((product) => ({
          id: product.id,
          externalId: product.externalId,
          ean: product.ean,
          articleNumber: product.articleNumber,
          acquisitionCost: product.acquisitionCost,
          title: product.title,
          price: product.price,
          quantity: product.quantity ?? 1,
          discount: product.discount ?? null,
        })),
        ...ReceiptMapper.processProductChoices(
          part.productChoices,
          submission.productDecisions,
        ),
      ]

      // Part-actions that fit within an existing package on the work order
      // have their labour covered by that package's flat price — only the
      // products are charged. Zero the labour here so the addon total (and the
      // nested package rendering) doesn't double-charge for work the customer
      // already paid for via the package.
      const isInExistingPackage = part.existingPackageSnapshotId !== null
      const discountedLaborPrice = isInExistingPackage
        ? 0
        : PriceUtils.applyDiscount(part.laborPrice, part.laborPriceDiscount)
      const totalProductCost = PriceUtils.getProductsCost(products)
      const totalPartActionCost = discountedLaborPrice + totalProductCost

      partActions.push({
        partId: part.partId,
        partName: part.partName,
        partAction: part.partAction,
        actionLabel: part.actionLabel,
        comment: part.comment ?? undefined,
        products,
        laborPrice: discountedLaborPrice,
        laborPriceOriginal: isInExistingPackage ? 0 : part.laborPrice,
        laborPriceAfterDiscount:
          !isInExistingPackage && part.laborPriceDiscount != null
            ? PriceUtils.getDiscountedPriceOrNull(
                part.laborPrice,
                part.laborPriceDiscount,
              )
            : null,
        laborPriceDiscount: isInExistingPackage
          ? null
          : part.laborPriceDiscount,
        totalProductCost,
        totalCost: totalPartActionCost,
      })

      totalCost += totalPartActionCost
    }

    // Process selected manual service actions
    const acceptedMsaIds = new Set(submission.acceptedManualServiceActionIds)
    for (const msa of proposal.manualServiceActions) {
      if (!acceptedMsaIds.has(msa.id)) continue

      const products: ReceiptProduct[] = [
        ...msa.products.map((product) => ({
          id: product.id,
          externalId: product.externalId,
          ean: product.ean,
          articleNumber: product.articleNumber,
          acquisitionCost: product.acquisitionCost,
          title: product.title,
          price: product.price,
          quantity: product.quantity ?? 1,
          discount: product.discount ?? null,
        })),
        ...ReceiptMapper.processProductChoices(
          msa.productChoices,
          submission.productDecisions,
        ),
      ]

      const totalProductCost = PriceUtils.getProductsCost(products)
      const effectiveMsaLaborPrice = PriceUtils.applyDiscount(
        msa.laborPrice,
        msa.laborPriceDiscount ?? null,
      )
      const totalManualActionCost = effectiveMsaLaborPrice + totalProductCost

      manualActions.push({
        id: msa.id,
        title: msa.name,
        comment: msa.comment ?? undefined,
        products,
        laborPrice: effectiveMsaLaborPrice,
        laborPriceOriginal: msa.laborPrice,
        laborPriceAfterDiscount: PriceUtils.getDiscountedPriceOrNull(
          msa.laborPrice,
          msa.laborPriceDiscount ?? null,
        ),
        laborPriceDiscount: msa.laborPriceDiscount ?? null,
        totalProductCost,
        totalCost: totalManualActionCost,
      })

      totalCost += totalManualActionCost
    }

    // Process selected packages with their action products
    const acceptedPackageIds = new Set(submission.acceptedPackageIds)
    for (const pkg of proposal.packages) {
      if (!acceptedPackageIds.has(pkg.id)) continue

      const laborPriceAfterDiscount = PriceUtils.getDiscountedPriceOrNull(
        pkg.laborPrice,
        pkg.laborPriceDiscount,
      )

      const partActions: ReceiptPackagePartAction[] = pkg.actions.map(
        (action) => ({
          partId: action.partId,
          partName: action.partName,
          partAction: action.action,
          actionLabel: action.actionLabel,
        }),
      )

      const packageProducts: ReceiptPackageProduct[] = pkg.actions.flatMap(
        (action) => {
          const resolvedProducts: ReceiptProduct[] = [
            ...action.products.map((product) => ({
              id: product.id,
              externalId: product.externalId,
              ean: product.ean,
              articleNumber: product.articleNumber,
              acquisitionCost: product.acquisitionCost,
              title: product.title,
              price: product.price,
              quantity: product.quantity ?? 1,
              discount: product.discount ?? null,
            })),
            ...ReceiptMapper.processProductChoices(
              action.productChoices,
              submission.productDecisions,
            ),
          ]
          return resolvedProducts.map((product) => ({
            ...product,
            partId: action.partId,
            partName: action.partName,
            partAction: action.action,
          }))
        },
      )

      const totalProductCost = PriceUtils.getProductsCost(packageProducts)
      const { total: totalPackageCost, materialLaborDeduction } =
        PriceUtils.getPackageTotal({
          laborPrice: pkg.laborPrice,
          laborPriceDiscount: pkg.laborPriceDiscount,
          totalProductCost,
          includedMaterialCost: pkg.includedMaterialCost,
        })

      packages.push({
        originalPackageId: pkg.id,
        snapshotId: pkg.id,
        title: pkg.packageName,
        wash: null,
        partActions,
        availableActions: [],
        products: packageProducts,
        laborPrice: pkg.laborPrice,
        laborPriceAfterDiscount,
        laborPriceDiscount: pkg.laborPriceDiscount,
        totalProductCost,
        includedMaterialCost: pkg.includedMaterialCost,
        materialLaborDeduction,
        totalCost: totalPackageCost,
      })

      totalCost += totalPackageCost
    }

    // Process selected wash from alternatives
    if (submission.selectedWashOriginalId && proposal.washAlternatives) {
      const selectedAlt = proposal.washAlternatives.alternatives.find(
        (alt) => alt.originalWashId === submission.selectedWashOriginalId,
      )
      if (selectedAlt) {
        wash = ReceiptMapper.washToReceiptWash(selectedAlt)
        totalCost += wash.price
      }
    }

    // Fees ride along with the addon order regardless of what the customer
    // accepts — they are not part of the approval workflow. They still belong
    // in the addon's own total so "Totalkostnad för tillägg" is honest.
    const fees: ReceiptFee[] = proposal.fees
      .filter((fee) => fee.cost > 0)
      .map(ReceiptMapper.proposalFeeToReceiptFee)
    const totalFeeCost = fees.reduce((sum, fee) => sum + fee.price, 0)
    totalCost += totalFeeCost

    const addonEffectiveLaborCost =
      packages.reduce(
        (sum, pkg) => sum + PriceUtils.getPackageLaborPrice(pkg),
        0,
      ) +
      partActions.reduce((sum, a) => sum + a.laborPrice, 0) +
      manualActions.reduce((sum, a) => sum + a.laborPrice, 0) +
      (wash?.price ?? 0) +
      totalFeeCost

    const addonTotalProductsCost =
      packages.reduce((sum, pkg) => sum + pkg.totalProductCost, 0) +
      partActions.reduce((sum, a) => sum + a.totalProductCost, 0) +
      manualActions.reduce((sum, a) => sum + a.totalProductCost, 0)

    return {
      packages,
      partActions,
      manualActions,
      wash,
      fees,
      totalCost,
      orderDiscount: null,
      totalCostAfterOrderDiscount: null,
      laborDiscount: null,
      laborCostAfterDiscount: null,
      productsDiscount: null,
      productsCostAfterDiscount: null,
      vat: Math.round(totalCost * (SERVICE_VAT / 100) * 100) / 100,
      customerBudget: null,
      effectiveLaborCost: addonEffectiveLaborCost,
      effectiveProductsCost: addonTotalProductsCost,
      totalDiscountGiven: 0,
    }
  }

  /**
   * Moves proposed part-actions that belong to an existing package on the work
   * order out of the flat `partActions` list and into a map keyed by the
   * package's snapshot id. `UnifiedReceipt` renders the mapped actions nested
   * under their package (with a + icon) instead of as standalone items, so the
   * sent/customer-facing receipt matches the mechanic's "Sammanfattning".
   *
   * The association comes from `AddonPartToAccept.existingPackageSnapshotId`,
   * matched back to the receipt part-actions by part id + action.
   */
  static splitProposedByExistingPackage(
    proposal: AddonOrderToAccept,
    proposedReceipt: Receipt,
  ): {
    adjustedProposedReceipt: Receipt
    proposedPackagePartActions: Map<string, ReceiptPartAction[]>
  } {
    const snapshotIdByPartActionKey = new Map<string, string>()
    for (const part of proposal.parts) {
      if (part.existingPackageSnapshotId === null) continue
      snapshotIdByPartActionKey.set(
        `${part.partId}:${part.partAction}`,
        part.existingPackageSnapshotId,
      )
    }

    const nested = new Map<string, ReceiptPartAction[]>()
    const standalone: ReceiptPartAction[] = []
    for (const partAction of proposedReceipt.partActions) {
      const snapshotId = snapshotIdByPartActionKey.get(
        `${partAction.partId}:${partAction.partAction}`,
      )
      if (snapshotId) {
        const list = nested.get(snapshotId) ?? []
        list.push(partAction)
        nested.set(snapshotId, list)
      } else {
        standalone.push(partAction)
      }
    }

    return {
      adjustedProposedReceipt: { ...proposedReceipt, partActions: standalone },
      proposedPackagePartActions: nested,
    }
  }
}
